/**
 * Escapes HTML special characters to prevent XSS attacks
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
export function escapeHtml(str) {
  if (typeof str !== 'string') {
    return String(str ?? '');
  }

  const escapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  };

  return str.replace(/[&<>"']/g, char => escapeMap[char]);
}

/**
 * Creates a DOM element with safe text content
 * @param {string} tag - HTML tag name
 * @param {object} attributes - Element attributes
 * @param {string|Node|Array<Node>} content - Text content or child nodes
 * @returns {HTMLElement} Created element
 */
export function createElement(tag, attributes = {}, content = null) {
  const element = document.createElement(tag);

  for (const [key, value] of Object.entries(attributes)) {
    if (key === 'className') {
      element.className = value;
    } else if (key === 'dataset') {
      for (const [dataKey, dataValue] of Object.entries(value)) {
        element.dataset[dataKey] = dataValue;
      }
    } else if (key.startsWith('on') && typeof value === 'function') {
      element.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (value !== null && value !== undefined) {
      element.setAttribute(key, value);
    }
  }

  if (content !== null) {
    if (typeof content === 'string') {
      element.textContent = content;
    } else if (content instanceof Node) {
      element.appendChild(content);
    } else if (Array.isArray(content)) {
      content.forEach(child => {
        if (child instanceof Node) {
          element.appendChild(child);
        } else if (typeof child === 'string') {
          element.appendChild(document.createTextNode(child));
        }
      });
    }
  }

  return element;
}

/**
 * Creates multiple elements and returns them as a document fragment
 * @param {Array<HTMLElement>} elements - Array of elements
 * @returns {DocumentFragment} Document fragment containing elements
 */
export function createFragment(elements) {
  const fragment = document.createDocumentFragment();
  elements.forEach(el => fragment.appendChild(el));
  return fragment;
}

/**
 * Safely sets innerHTML with escaped content
 * Use only when you need HTML structure but with escaped user data
 * @param {HTMLElement} element - Target element
 * @param {string} template - HTML template
 * @param {object} values - Values to interpolate (will be escaped)
 */
export function setInnerHtmlSafe(element, template, values = {}) {
  let result = template;

  for (const [key, value] of Object.entries(values)) {
    const placeholder = `{{${key}}}`;
    result = result.replace(new RegExp(placeholder, 'g'), escapeHtml(value));
  }

  element.innerHTML = result;
}

/**
 * Creates a product card element safely without innerHTML
 * @param {object} product - Product data
 * @param {object} options - Card options
 * @returns {HTMLElement} Card element
 */
export function createProductCard(product, options = {}) {
  const { quantity = 1, dossierProduitId = null } = options;

  const prixHt = parseFloat(product.option_prix_ht || product.dossier_produit_prix_ht || 0);
  const remise = parseFloat(
    product.option_remise_conseillee || product.dossier_produit_remise || 0
  );
  const prixNet = prixHt * (1 - remise / 100);
  const total = prixNet * quantity;

  const col = createElement(
    'div',
    {
      className: 'col-12 col-md-6 col-lg-4 mb-3',
      dataset: {
        productId: product.option_id,
        ...(dossierProduitId && { dossierProduitId })
      }
    },
    [
      createElement('div', { className: 'card' }, [
        // Card header
        createElement('div', { className: 'card-header d-flex justify-content-between align-items-center' }, [
          createElement('h5', { className: 'card-title mb-0' }, `${product.option_reference || ''} [${product.produit_id || product.option_id}]`),
          createElement('button', {
            type: 'button',
            className: 'btn-close',
            'aria-label': 'Close'
          })
        ]),
        // Card body
        createElement('div', { className: 'card-body' }, [
          // Product name (if available)
          ...(product.option_description || product.dossier_produit_nom
            ? [
                createElement(
                  'p',
                  { className: 'text-muted small' },
                  product.option_description || product.dossier_produit_nom
                )
              ]
            : []),
          // Pricing info
          createPricingElement(prixHt, remise, prixNet),
          // Quantity and total row
          createElement('div', { className: 'd-flex justify-content-between align-items-center' }, [
            createElement('div', { className: 'input-group w-50' }, [
              createElement('span', { className: 'input-group-text' }, 'Qté'),
              createElement('input', {
                type: 'number',
                className: 'form-control quantity-input',
                value: String(quantity),
                min: '1'
              })
            ]),
            createElement('p', { className: 'card-text mb-0' }, [
              createElement('strong', {}, 'Total: '),
              createElement('span', { className: 'total-card' }, total.toFixed(2))
            ])
          ])
        ])
      ])
    ]
  );

  return col;
}

/**
 * Creates pricing element for card
 * @param {number} prixHt - Gross price
 * @param {number} remise - Discount percentage
 * @param {number} prixNet - Net price
 * @returns {HTMLElement} Pricing paragraph element
 */
function createPricingElement(prixHt, remise, prixNet) {
  const p = createElement('p', { className: 'card-text' });

  p.appendChild(createElement('strong', {}, 'P. Brut:'));
  p.appendChild(document.createTextNode(` ${prixHt.toFixed(2)} `));
  p.appendChild(createElement('br'));

  p.appendChild(createElement('strong', {}, 'Remise:'));
  p.appendChild(document.createTextNode(` ${remise}% `));
  p.appendChild(createElement('br'));

  p.appendChild(createElement('strong', { className: 'prix-net-label' }, 'P. Net:'));
  p.appendChild(document.createTextNode(' '));
  p.appendChild(createElement('span', { className: 'prix-net' }, prixNet.toFixed(2)));

  return p;
}

/**
 * Updates pricing display in a card element
 * @param {HTMLElement} cardBody - Card body element
 * @param {number} prixHt - Gross price
 * @param {number} remise - Discount percentage
 * @param {number} prixNet - Net price
 */
export function updatePricingDisplay(cardBody, prixHt, remise, prixNet) {
  const pricingElements = cardBody.querySelectorAll('.card-text');

  for (const element of pricingElements) {
    if (element.querySelector('.prix-net')) {
      const newPricing = createPricingElement(prixHt, remise, prixNet);
      element.replaceWith(newPricing);
      break;
    }
  }
}

/**
 * Creates an alert element safely
 * @param {string} message - Alert message (will be escaped)
 * @param {string} variant - Alert variant (danger, success, warning, info)
 * @returns {HTMLElement} Alert element
 */
export function createAlertElement(message, variant = 'danger') {
  const alertEl = document.createElement('alert-element');
  alertEl.setAttribute('variant', variant);
  alertEl.setAttribute('dismissible', '');
  alertEl.setAttribute('open', '');

  // Create icon slot
  const iconSlot = createElement('span', { slot: 'icon' });
  iconSlot.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="1.25em" height="1.25em" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4.54.146A.5.5 0 0 1 4.893 0h6.214a.5.5 0 0 1 .353.146l4.394 4.394a.5.5 0 0 1 .146.353v6.214a.5.5 0 0 1-.146.353l-4.394 4.394a.5.5 0 0 1-.353.146H4.893a.5.5 0 0 1-.353-.146L.146 11.46A.5.5 0 0 1 0 11.107V4.893a.5.5 0 0 1 .146-.353zM5.1 1 1 5.1v5.8L5.1 15h5.8l4.1-4.1V5.1L10.9 1z"/>
      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
    </svg>
  `;

  alertEl.appendChild(iconSlot);
  alertEl.appendChild(document.createTextNode(message));

  return alertEl;
}

/**
 * Creates camera error alert safely
 * @param {string} errorMessage - Error message
 * @returns {HTMLElement} Alert element
 */
export function createCameraErrorAlert(errorMessage) {
  const alertEl = document.createElement('alert-element');
  alertEl.setAttribute('variant', 'danger');
  alertEl.setAttribute('open', '');

  const iconSlot = createElement('span', { slot: 'icon' });
  iconSlot.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="1.25em" height="1.25em" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4.54.146A.5.5 0 0 1 4.893 0h6.214a.5.5 0 0 1 .353.146l4.394 4.394a.5.5 0 0 1 .146.353v6.214a.5.5 0 0 1-.146.353l-4.394 4.394a.5.5 0 0 1-.353.146H4.893a.5.5 0 0 1-.353-.146L.146 11.46A.5.5 0 0 1 0 11.107V4.893a.5.5 0 0 1 .146-.353zM5.1 1 1 5.1v5.8L5.1 15h5.8l4.1-4.1V5.1L10.9 1z"/>
      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
    </svg>
  `;

  alertEl.appendChild(iconSlot);

  // Add error message - use innerHTML only for trusted content
  const messageSpan = createElement('span');
  messageSpan.innerHTML = escapeHtml(errorMessage).replace(
    /&lt;br&gt;/g,
    '<br>'
  );
  alertEl.appendChild(messageSpan);

  return alertEl;
}
